import cn from 'classnames'
import Image from 'next/image'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ArrowIcon} from '@/components/common/icons/ArrowIcon'
import {team} from '@/content/team'
import {getTeamContent} from '@/content/teamContent'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {routeTo} from '@/utils/router'
import {
  MOBILE_SCREENSIZE,
  TABLET_SCREENSIZE,
  USER_TABLET_SCREENSIZE,
} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {ImageChip} from '../content/ImageChip'
import {SectionLayout} from '../content/SectionLayout'
import {Text, TextVariant} from '../content/Text'

type LicensedAttorneysProps = {
  isReversed?: boolean
  chipBackgroundColor?: string
}

export const LicensedAttorneys: FC<LicensedAttorneysProps> = ({
  isReversed,
  chipBackgroundColor,
}) => {
  const {t} = useTranslation()
  const {title, description, labels, button, teamMembers} = getTeamContent().licensedAttorneys

  const handleClick = () => analytics.track(button.trackedEvent)

  // Rendered in both places and toggled in CSS, so the button doesn't jump after hydration
  const learnMoreButton = (
    <Link href={button.linkHref} onClick={handleClick}>
      <Button type="button" icon={<ArrowIcon />} isHoverable>
        {t(button.linkText)}
      </Button>
    </Link>
  )

  return (
    <>
      <SectionLayout
        title={{text: t(title)}}
        description={{text: t(description), maxWidth: '570px'}}
        align="left"
        orientation="horizontal"
        isReversed={isReversed}
        additionalContent={
          <>
            <div className="labels">
              {labels.map(({imageSrc, imageAlt, title}, index) => (
                <ImageChip
                  key={index}
                  title={t(title)}
                  imageSrc={imageSrc}
                  imageAlt={imageAlt}
                  backgroundColor={chipBackgroundColor}
                />
              ))}
            </div>
            <div className="button-wrapper desktop">{learnMoreButton}</div>
          </>
        }
      >
        <div className={cn('team', isReversed && 'reversed')}>
          {teamMembers.map(({photoSrc, photoAlt, alternativePhotoSrc}, index) => (
            <div key={index} className="item">
              {/* Lazy by default (no priority) — this section is below the fold.
                  Styles are inline, not a .photo class: styled-jsx does not scope className onto
                  a component, so the rule would never match. width/height stay intrinsic (not
                  `fill`) because the grid is shrink-to-fit and sizes its columns from the images'
                  intrinsic width — absolutely-positioned fill images collapse it to ~0. */}
              <Image
                src={alternativePhotoSrc ?? photoSrc}
                alt={photoAlt}
                width={136}
                height={136}
                sizes={`${MOBILE_SCREENSIZE} 33vw, 136px`}
                style={{display: 'block', width: '100%', height: '100%', objectFit: 'cover'}}
              />
            </div>
          ))}
          <div className="item">
            <Link href={routeTo.aboutUs.home()} legacyBehavior>
              <a className="show-more">
                <Text variant={TextVariant.h4} element="p" color={theme.palette.text.white}>
                  +{Object.keys(team).length - teamMembers.length}
                </Text>
              </a>
            </Link>
          </div>
        </div>
        <div className="button-wrapper tablet">{learnMoreButton}</div>
      </SectionLayout>

      <style jsx>{`
        .labels {
          display: flex;
          flex-wrap: wrap;
          gap: ${theme.spacing[2]};
          row-gap: ${theme.spacing[3]};
          margin-top: ${theme.spacing[5]};
          margin-left: -${theme.spacing[1]};
        }
        .team {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: ${theme.spacing[3]};
          /* Definite width, not max-width: this grid is a shrink-to-fit flex item, so a
             content-derived width depends on the images' intrinsic size — which is unreliable
             once they are next/image (srcset + width: 100%) and collapses the columns. */
          width: 440px;
          max-width: 100%;
          /* This grid is a flex item; without this it shrinks below 440px, because the images no
             longer contribute an intrinsic min-content width to resist it. */
          flex-shrink: 0;
          margin-left: ${theme.spacing[7]};
        }
        .item {
          border-radius: 12px;
          overflow: hidden;
          /* The tiles size themselves rather than inheriting height from the loaded photo. The
             image sets height: 100%, which overrides the aspect ratio its width/height attributes
             would otherwise supply, so without this the rows collapse until the photos arrive.
             The gray fill shows through as a placeholder until then. */
          aspect-ratio: 1;
          background-color: ${theme.palette.gray[200]};
          /* Containing block for the nth-child(4) gradient overlay. */
          position: relative;
        }
        .item:has(.show-more:focus-visible) {
          outline: var(--focus-outline);
          outline-offset: 2px;
        }
        .item:nth-child(4) {
          /* Spans two rows, so its height comes from the row track, not its own aspect ratio. */
          aspect-ratio: auto;
          grid-row: span 2;
        }
        .item:nth-child(4)::after {
          content: '';
          width: 100%;
          height: 50%;
          position: absolute;
          bottom: 0;
          left: 0;
          background: linear-gradient(180deg, rgba(229, 106, 97, 0) 0%, #e56a61 100%);
        }
        .show-more {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: linear-gradient(
              358deg,
              rgba(229, 106, 97, 0.2) 16.96%,
              rgba(246, 224, 223, 0.2) 102.76%
            ),
            linear-gradient(332deg, #e56a61 49.31%, #f19881 110.16%);
        }
        .reversed {
          margin-left: 0;
          margin-right: ${theme.spacing[8.5]};
        }
        .desktop {
          margin-top: ${theme.spacing[5]};
        }
        .tablet {
          display: none;
        }
        @media ${TABLET_SCREENSIZE} {
          .reversed {
            margin-right: ${theme.spacing[8]};
          }
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .labels {
            justify-content: center;
          }
          .team {
            margin-left: 0;
            margin-top: ${theme.spacing[6]};
          }
          .reversed {
            margin-right: 0;
          }
          .desktop {
            display: none;
          }
          .tablet {
            display: block;
            margin-top: ${theme.spacing[5.5]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .labels {
            justify-content: flex-start;
          }
          .team {
            gap: ${theme.spacing[2.5]};
          }
          .team {
            margin-top: ${theme.spacing[5.5]};
          }
        }
      `}</style>
    </>
  )
}
